import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PropTypes from "prop-types";
import PeoplePicker from "../sharing/PeoplePicker";
import { sharePayload } from "../sharing/sharePayload";
import { parseApiError } from "../../services/apiError";

/**
 * Create/edit modal for projects.
 *
 * If `project` is passed, the modal opens in edit mode. Otherwise it opens
 * in create mode. Submit calls onSave(data), which the parent binds to
 * createProject or updateProject. Errors from the API are displayed
 * inline on the relevant field when the response looks like DRF field
 * errors (`{ name: ["..."] }`), or as a form-level banner otherwise.
 */
function ProjectModal({
  show,
  onHide,
  project,
  onSave,
  canShareWithPeople = false,
}) {
  const isEdit = Boolean(project);
  // Sharing outward is a person's decision (the API refuses agents), and
  // it stays hidden until we KNOW the viewer is a person — fail closed.
  // An existing people project still shows its setting, read-only.
  const showPeople = canShareWithPeople || project?.visibility === "people";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("private");
  // household sharing D2: the people a "Specific people" project is shared
  // with ({id, username}); added one at a time by exact username or email
  const [people, setPeople] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (show) {
      setName(project?.name || "");
      setDescription(project?.description || "");
      setVisibility(project?.visibility || "private");
      setPeople(
        (project?.shared_with || []).map((p) => ({
          id: p.id,
          username: p.username,
        })),
      );
      setFieldErrors({});
      setFormError(null);
    }
  }, [show, project]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        visibility,
        // the list only means something for "Specific people" (switching
        // away forgets it server-side); only a person may change it
        ...(visibility === "people" && canShareWithPeople
          ? sharePayload(people)
          : {}),
      });
      onHide();
    } catch (err) {
      // Shared extractor splits DRF `{detail}`/`non_field_errors` (form-level)
      // from per-field errors (`{ name: [...] }`), which the fields render inline.
      const { formError: banner, fieldErrors: fields } = parseApiError(
        err,
        "Failed to save project",
      );
      setFieldErrors(fields);
      setFormError(banner);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onHide();
  };

  const nameError = fieldErrors.name
    ? [].concat(fieldErrors.name).join(" ")
    : null;
  const descriptionError = fieldErrors.description
    ? [].concat(fieldErrors.description).join(" ")
    : null;

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        showCloseButton={!saving}
        className="p-0"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => saving && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          nameRef.current?.focus();
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader className="border-b px-6 py-4 text-left">
            <DialogTitle className="font-display text-xl">
              {isEdit ? "Edit project" : "New project"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isEdit ? "Edit this project" : "Create a project"}
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4">
            {formError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="mb-4 grid gap-1.5">
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name"
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={Boolean(nameError) || undefined}
                aria-describedby={nameError ? "project-name-error" : undefined}
                required
              />
              {nameError && (
                <p id="project-name-error" className="text-sm text-destructive">
                  {nameError}
                </p>
              )}
            </div>

            <div className="mb-4 grid gap-1.5">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                className="max-h-48"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                aria-invalid={Boolean(descriptionError) || undefined}
                aria-describedby={
                  descriptionError ? "project-description-error" : undefined
                }
              />
              {descriptionError && (
                <p
                  id="project-description-error"
                  className="text-sm text-destructive"
                >
                  {descriptionError}
                </p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label id="project-visibility-label">Who can see it</Label>
              {/* Two named modes, one control: the frontend philosophy's
                  "the user always knows which mode they're in". A single
                  ToggleGroup (role=radiogroup) that can't be deselected. */}
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={visibility}
                onValueChange={(next) => next && setVisibility(next)}
                aria-labelledby="project-visibility-label"
                className="justify-start"
              >
                <ToggleGroupItem
                  id="project-visibility-private"
                  value="private"
                >
                  Private
                </ToggleGroupItem>
                <ToggleGroupItem
                  id="project-visibility-household"
                  value="household"
                >
                  Household
                </ToggleGroupItem>
                {showPeople && (
                  <ToggleGroupItem
                    id="project-visibility-people"
                    value="people"
                  >
                    Specific people
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
              <p className="text-sm text-muted-foreground">
                {visibility === "household"
                  ? "Your household — its people and their agents — can see and edit its tasks. Only you can rename, re-share or delete the project."
                  : visibility === "people"
                    ? "The people you add — and their agents — can see and edit its tasks. Only you can rename, re-share or delete the project."
                    : "Only you and your agents. Tasks in it can't be assigned to anyone else."}
              </p>
              {visibility === "people" && (
                <PeoplePicker
                  idPrefix="project"
                  people={people}
                  onChange={setPeople}
                  canEdit={canShareWithPeople}
                  readOnlyReason="Only a person can change who it's shared with."
                  serverError={
                    fieldErrors.shared_with_ids || fieldErrors.share_with
                      ? []
                          .concat(fieldErrors.shared_with_ids ?? [])
                          .concat(fieldErrors.share_with ?? [])
                          .join(" ")
                      : null
                  }
                />
              )}
              {fieldErrors.visibility && (
                <p className="text-sm text-destructive">
                  {[].concat(fieldErrors.visibility).join(" ")}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" />
                  Saving...
                </>
              ) : isEdit ? (
                "Save"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

ProjectModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    visibility: PropTypes.string,
    shared_with: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, username: PropTypes.string }),
    ),
  }),
  onSave: PropTypes.func.isRequired,
  canShareWithPeople: PropTypes.bool,
};

export default ProjectModal;
